import Button, { ButtonTypes } from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent } from "molecules/modals/modal";
import React, { useEffect, useState } from "react";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Heading from "atoms/typography/heading";
import PageRecord from "models/view-models/page-record";
import InputFormField from "molecules/form-fields/input-form-field";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import FileRecord from "models/view-models/file-record";
import { t } from "utilities/localization-utils";
import ImagePreviewField from "molecules/form-fields/image-preview-field";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import useFile from "utilities/hooks/domain/files/use-file";

interface EditPageModalProps {
    page: PageRecord;
    isVisible: boolean;
    closeDialog: (event?: CloseDialogEvent) => void;
}

const BASE_CLASS_NAME = "c-edit-page-modal";

const EditPageModal = (props: EditPageModalProps) => {
    const { isVisible, closeDialog, page } = props;
    const [newPageTitle, setNewPageTitle] = useState<string>("");
    const [coverImage, setCoverImage] = useState<FileRecord | undefined>(
        undefined
    );
    const [isValidPageName, setIsValidPageName] = useState<boolean>(true);

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const { updatePage } = useNotebooksAtom();
    const { file } = useFile(page.coverImageFileId);

    const onPageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewPageTitle(value);

        if (!isValidPageName && value.trim().length > 0) {
            setIsValidPageName(true);
        }
    };

    const onCoverImageChange = (newCoverImage?: FileRecord) => {
        setCoverImage(newCoverImage);
    };

    const onClickCancel = () => {
        closeDialog();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await updatePage(
            page.with({ name: newPageTitle, coverImageFileId: coverImage?.id })
        );
        closeDialog();
    };

    // Populate with current page details
    useEffect(() => {
        setNewPageTitle(page.name);
        setCoverImage(file);
    }, [file, page.name]);

    return (
        <Modal
            stopEventPropagation
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeDialog}
            cssClassName={BASE_CLASS_NAME}
            isVisible={isVisible}
            onCloseComplete={onClickCancel}
            label={t("actionItem", { action: t("edit"), item: t("page") })}
            type={modalType}>
            <Heading cssClassName={`${BASE_CLASS_NAME}__heading`}>
                {t("actionItem", { action: t("edit"), item: t("page") })}
                <Button
                    accessibleText={t("close")}
                    cssClassName={"-modal-close -icon"}
                    onClick={closeDialog}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.TertiaryAlt}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <form
                className={`${BASE_CLASS_NAME}__form`}
                onSubmit={handleSubmit}>
                <div className={`${BASE_CLASS_NAME}__input`}>
                    <InputFormField
                        inputTestId="edit-page-modal--page-title-input"
                        placeholder={t("notebooks-create-placeholder")}
                        onChange={onPageNameChange}
                        label={t("notebooks-create-label")}
                        value={newPageTitle}
                        maxLength={60}
                        isValid={isValidPageName}
                    />
                </div>
                <ImagePreviewField
                    image={coverImage}
                    imageDestination={FileUploadDestination.NotebookImage}
                    onAdd={onCoverImageChange}
                    onDelete={() => onCoverImageChange(undefined)}
                />
                <div className={`${BASE_CLASS_NAME}__buttons`}>
                    <Button
                        accessibleText={t("cancel")}
                        onClick={onClickCancel}
                        style={ButtonStyles.Secondary}
                        dataTestId="edit-page-modal--cancel-button">
                        {t("cancel")}
                    </Button>
                    <Button
                        accessibleText={t("save")}
                        style={ButtonStyles.Primary}
                        dataTestId="edit-page-modal--save-button"
                        type={ButtonTypes.Submit}>
                        {t("save")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default EditPageModal;
